'use client'

import { useState } from 'react'
import { Button } from '@teqplay/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBookmarkSlash,
  faSpinner,
  faChevronDown,
  faChevronUp
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { User } from '@auth0/nextjs-auth0/types'
import { faBookmark, faClock } from '@fortawesome/pro-regular-svg-icons'

import ModalSharingVesselForm from '../Modals/ModalSharingVesselForm'
import DateClient from '../ClientDateFormat/ClientDateFormat'
import InfoToolTip from '../ToolTipProvider/ToolTipProvider'

import { Link } from '@/i18n/navigation'
import type { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import useWaitingStatus from '@/app/hooks/useWaitingStatus'

export type BookmarkCardProps = {
  bookmark: TVesselJourneyResponse
  t: ReturnType<typeof useTranslations>
  user?: User | null
  active?: boolean
  hide?: boolean
  onRemoveBookmark: (vessel: TVesselJourneyResponse) => void
  getIsVesselBookmarked: (imo: string) => boolean
  addBookmarkNow: (vessel: TVesselJourneyResponse) => void
  isBookmarkLoading?: boolean
}

export default function BookmarkCard({
  bookmark,
  t,
  active,
  hide,
  onRemoveBookmark,
  getIsVesselBookmarked,
  addBookmarkNow,
  isBookmarkLoading = false
}: BookmarkCardProps) {
  const [openShare, setOpenShare] = useState<boolean>(false)

  const { setIsWaiting: setIsWaitingAfterSuccessShare } = useWaitingStatus()

  // Check if vessel is actually bookmarked (regardless of tab)
  const actuallyBookmarked = getIsVesselBookmarked(bookmark.ship.imo)

  // Check if vessel has already arrived
  const hasArrived = bookmark.arrival?.actualTime
    ? new Date(bookmark.arrival.actualTime).getTime() < Date.now()
    : false

  if (hide) {
    return null
  }

  const cardId = `bookmark-card-${bookmark.ship.imo}`

  return (
    <li
      className={clsx('bookmark-card', { active })}
      key={bookmark.ship.imo}
      data-vessel-imo={bookmark.ship.imo}
    >
      {/* Hidden checkbox for CSS-only toggle */}
      <input
        type="checkbox"
        id={cardId}
        className="bookmark-card-toggle-checkbox"
        defaultChecked={active}
        aria-label={active ? 'Collapse vessel details' : 'Expand vessel details'}
      />

      <div className="bookmark-card-header">
        <div className="info-container">
          <Link href={`/vessels/${bookmark.ship.imo}`} className="ship-info" prefetch={false}>
            <p className="h3">{bookmark.ship.name || bookmark.ship.imo}</p>
            <span className="eta-ata-time">
              <FontAwesomeIcon icon={faClock} />
              {bookmark.arrival?.actualTime || bookmark.arrival?.estimatedTime ? (
                <>
                  <div>{hasArrived ? t('Bookmarks.ata') : t('Bookmarks.eta')}</div>
                  <DateClient
                    date={
                      hasArrived ? bookmark.arrival?.actualTime : bookmark.arrival?.estimatedTime
                    }
                  />
                </>
              ) : (
                t('common.unknown')
              )}
            </span>
          </Link>
        </div>

        <div className="bookmark-card-header-actions">
          {actuallyBookmarked ? (
            <Button
              variant="brand-secondary"
              size="s"
              aria-label="Unfollow"
              onClick={() => onRemoveBookmark(bookmark)}
              disabled={isBookmarkLoading}
            >
              <FontAwesomeIcon
                icon={isBookmarkLoading ? faSpinner : faBookmarkSlash}
                spin={isBookmarkLoading}
              />
            </Button>
          ) : (
            <InfoToolTip messageKey="trackAndTrace">
              <Button
                variant="brand-secondary"
                size="s"
                aria-label="Add bookmark"
                onClick={() => addBookmarkNow(bookmark)}
                disabled={isBookmarkLoading}
              >
                <FontAwesomeIcon
                  icon={isBookmarkLoading ? faSpinner : faBookmark}
                  spin={isBookmarkLoading}
                />
              </Button>
            </InfoToolTip>
          )}

          <label htmlFor={cardId} className="bookmark-card-toggle-button">
            <FontAwesomeIcon icon={faChevronDown} className="toggle-icon-expand" />
            <FontAwesomeIcon icon={faChevronUp} className="toggle-icon-collapse" />
          </label>
        </div>
      </div>

      {/* Expandable content */}
      <div className="bookmark-card-expandable">
        <div className="tags">
          <div className="tag">
            <div className="ship-history-row">
              <div>{bookmark.departure?.port?.name || t('common.unknown')}</div>
              <FontAwesomeIcon icon={faArrowRight} className="ship-arrow" />
              <div>{bookmark.arrival?.port?.name || t('common.unknown')}</div>
            </div>
          </div>
        </div>
      </div>

      <ModalSharingVesselForm
        vessel={bookmark}
        open={openShare}
        setOpen={setOpenShare}
        onSharedSuccess={() => setIsWaitingAfterSuccessShare(true)}
      />
    </li>
  )
}
